'use client';
import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export type TipoRegistro = 'cliente' | 'cotizacion' | 'reserva' | 'pago' | 'contrato' | 'paquete' | 'servicio' | 'colaborador' | 'empresa_aliada' | 'evaluacion' | 'usuario';

export interface RegistroEliminado {
  id: string;
  tipo: TipoRegistro;
  nombre: string;
  descripcion: string;
  eliminadoPor: string;
  fechaEliminacion: string;
  diasRestantes: number;
  datos?: Record<string, unknown>;
}

export interface HistorialAccion {
  id: string;
  usuario: string;
  accion: string;
  modulo: string;
  descripcion: string;
  fecha: string;
  hora: string;
  tipo: 'crear' | 'editar' | 'eliminar' | 'restaurar' | 'imprimir' | 'descargar' | 'whatsapp' | 'correo' | 'login' | 'logout' | 'respaldo' | 'restablecer';
}

// ─── localStorage keys ────────────────────────────────────────────────────────

export const LS_KEYS = {
  PAPELERA: 'ega_papelera',
  HISTORIAL: 'ega_historial',
  CLIENTES: 'ega_clientes',
  COTIZACIONES: 'ega_cotizaciones',
  RESERVAS: 'ega_reservas',
  PAGOS: 'ega_pagos',
  CONTRATOS: 'ega_contratos',
  PAQUETES: 'ega_paquetes',
  SERVICIOS: 'ega_servicios',
  AGENDA: 'ega_agenda',
  CATEGORIAS: 'ega_categorias',
  COLABORADORES: 'ega_colaboradores',
  EMPRESAS_ALIADAS: 'ega_empresas_aliadas',
  CONTRATOS_COLABORACION: 'ega_contratos_colaboracion',
  EVALUACIONES: 'ega_evaluaciones',
  USUARIOS: 'ega_usuarios',
  SESION_ACTIVA: 'ega_sesion_activa',
  RESET_COUNT: 'ega_reset_count',
  INITIALIZED: 'ega_initialized',
} as const;

// ─── localStorage helpers ─────────────────────────────────────────────────────

export function lsGet<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function lsSet<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // quota exceeded or private mode
  }
}

export function lsRemove(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

// ─── Context Interface ────────────────────────────────────────────────────────

interface AppDataContextValue {
  // Papelera
  papelera: RegistroEliminado[];
  agregarAPapelera: (item: Omit<RegistroEliminado, 'diasRestantes'>) => void;
  restaurarDePapelera: (id: string) => RegistroEliminado | undefined;
  eliminarDefinitivoDePapelera: (id: string) => void;
  vaciarPapelera: () => void;

  // Historial / Auditoría
  historial: HistorialAccion[];
  registrarAccion: (accion: Omit<HistorialAccion, 'id' | 'fecha' | 'hora'>) => void;
  limpiarHistorial: () => void;

  // Factory Reset
  factoryReset: () => void;
  resetCount: number;

  // Initialization flag
  isInitialized: boolean;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const AppDataContext = createContext<AppDataContextValue | null>(null);

export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData must be used inside AppDataProvider');
  return ctx;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AppDataProvider({ children }: { children: ReactNode }) {
  const [papelera, setPapeleraState] = useState<RegistroEliminado[]>([]);
  const [historial, setHistorialState] = useState<HistorialAccion[]>([]);
  const [resetCount, setResetCount] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    const savedPapelera = lsGet<RegistroEliminado[]>(LS_KEYS.PAPELERA, []);
    const savedHistorial = lsGet<HistorialAccion[]>(LS_KEYS.HISTORIAL, []);
    const savedResetCount = lsGet<number>(LS_KEYS.RESET_COUNT, 0);
    setPapeleraState(savedPapelera);
    setHistorialState(savedHistorial);
    setResetCount(savedResetCount);
    setIsInitialized(true);
  }, []);

  // Persist papelera
  const setPapelera = useCallback((updater: RegistroEliminado[] | ((prev: RegistroEliminado[]) => RegistroEliminado[])) => {
    setPapeleraState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      lsSet(LS_KEYS.PAPELERA, next);
      return next;
    });
  }, []);

  // Persist historial
  const setHistorial = useCallback((updater: HistorialAccion[] | ((prev: HistorialAccion[]) => HistorialAccion[])) => {
    setHistorialState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      // Keep only last 500 entries to avoid quota issues
      const trimmed = next.slice(0, 500);
      lsSet(LS_KEYS.HISTORIAL, trimmed);
      return trimmed;
    });
  }, []);

  const agregarAPapelera = useCallback((item: Omit<RegistroEliminado, 'diasRestantes'>) => {
    const entry: RegistroEliminado = { ...item, diasRestantes: 30 };
    setPapelera(prev => [entry, ...prev]);
  }, [setPapelera]);

  const restaurarDePapelera = useCallback((id: string) => {
    let found: RegistroEliminado | undefined;
    setPapelera(prev => {
      found = prev.find(r => r.id === id);
      return prev.filter(r => r.id !== id);
    });
    return found;
  }, [setPapelera]);

  const eliminarDefinitivoDePapelera = useCallback((id: string) => {
    setPapelera(prev => prev.filter(r => r.id !== id));
  }, [setPapelera]);

  const vaciarPapelera = useCallback(() => {
    setPapelera([]);
  }, [setPapelera]);

  const registrarAccion = useCallback((accion: Omit<HistorialAccion, 'id' | 'fecha' | 'hora'>) => {
    const now = new Date();
    const entry: HistorialAccion = {
      ...accion,
      id: `hist-${Date.now()}`,
      fecha: now.toLocaleDateString('es-HN', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      hora: now.toLocaleTimeString('es-HN', { hour: '2-digit', minute: '2-digit' }),
    };
    setHistorial(prev => [entry, ...prev]);
  }, [setHistorial]);

  const limpiarHistorial = useCallback(() => {
    setHistorial([]);
  }, [setHistorial]);

  const factoryReset = useCallback(() => {
    // Clear ALL localStorage keys for this app
    Object.values(LS_KEYS).forEach(key => lsRemove(key));
    // Reset all state
    setPapeleraState([]);
    setHistorialState([]);
    const newCount = resetCount + 1;
    setResetCount(newCount);
    lsSet(LS_KEYS.RESET_COUNT, newCount);
    // Mark as initialized but fresh
    lsSet(LS_KEYS.INITIALIZED, true);
  }, [resetCount]);

  return (
    <AppDataContext.Provider value={{
      papelera,
      agregarAPapelera,
      restaurarDePapelera,
      eliminarDefinitivoDePapelera,
      vaciarPapelera,
      historial,
      registrarAccion,
      limpiarHistorial,
      factoryReset,
      resetCount,
      isInitialized,
    }}>
      {children}
    </AppDataContext.Provider>
  );
}
